import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, ArrowLeft, ChevronRight, Instagram } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { TEMPLES, templeSlug, type Temple } from './Temples';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1604608672516-f1b9b1d37076?w=1200&h=800&fit=crop';

function mapsUrl(t: Temple) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${t.name}, ${t.address}`)}`;
}

export default function TempleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('temples', TEMPLES, (t, i) =>
    slugify(t.name || String(i))
  );

  const entry: Temple | undefined = mc.items.find(
    (t: any) => templeSlug(t.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Temple Not Found | Dallas Desi Scoop"
          description="This temple listing could not be found."
          canonical="/temples"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Temple not found.</p>
        <Link
          to="/temples"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Temples
        </Link>
      </div>
    );
  }

  const isSpiritualService = (entry.type || 'Temple') === 'Spiritual Services';
  const isBAPS = /baps/i.test(entry.name || '');
  const isDallasTexasTemple = /dallas texas temple/i.test(entry.name || '');
  const isKaryaSiddhi = /karya siddhi/i.test(entry.name || '');
  const kindLabel = isSpiritualService ? 'Spiritual Services' : 'Hindu Temple';
  const city = entry.city || entry.address?.split(',').slice(-2, -1)[0]?.trim() || 'DFW';
  const title = `${entry.name} in ${city}, TX | ${kindLabel} | Dallas Desi Scoop`;
  const description = entry.detailDescription
    ? entry.detailDescription
    : isSpiritualService
    ? `${entry.name} offers spiritual services in ${city}, TX. Find address, phone number, and contact info.`
    : `${entry.name} is a Hindu temple in ${city}, TX. Find puja timings, address, directions, and contact info for this DFW temple.`;
  const canonicalPath = `/temples/${slug}`;

  // Split the stored address into a street portion and a locality (city) so the
  // structured-data PostalAddress carries a real addressLocality field, not just
  // one combined string. Supported shapes: "Street, City, TX Zip" (3 parts),
  // "City, TX" (2 parts), "City" (1 part).
  const addressParts = (entry.address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': isSpiritualService ? 'LocalBusiness' : 'HinduTemple',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: entry.detailDescription
        ? entry.detailDescription
        : isSpiritualService
        ? 'Spiritual services'
        : 'Hindu temple and place of worship',
      address: {
        '@type': 'PostalAddress',
        streetAddress: streetAddress,
        addressLocality: addressLocality || undefined,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.phone || undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Temples', item: `${BASE_URL}/temples` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/temples"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Temples
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            <span className="text-white/40">·</span>
            <span className="text-white/75 text-[13px]">{kindLabel}</span>
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          {entry.detailDescription ? (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {entry.detailDescription}
            </p>
          ) : isBAPS ? (
            <>
              <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
                BAPS Shri Swaminarayan Mandir is a Hindu temple and place of worship in Irving, Texas, serving the Dallas–Fort Worth community. The mandir is part of the BAPS Swaminarayan tradition and offers daily darshan and aarti, along with worship, festivals, ceremonies and community activities.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <h3 className="text-[14px] font-semibold text-[#082f6d] mb-1">Darshan Timings</h3>
                  <p className="text-[13px] text-gray-600 leading-relaxed">
                    Daily: 7:00 AM–11:00 AM, 11:30 AM–12:00 PM, and 4:30 PM–7:30 PM
                  </p>
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold text-[#082f6d] mb-1">Aarti</h3>
                  <p className="text-[13px] text-gray-600 leading-relaxed">
                    Daily: 7:30 AM and 7:00 PM
                  </p>
                </div>
              </div>
            </>
          ) : isDallasTexasTemple ? (
            <>
              <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
                Dallas Texas Temple is a temple of The Church of Jesus Christ of Latter-day Saints located in Dallas, Texas. The temple serves Latter-day Saints in the Dallas area and surrounding parts of Texas and Louisiana. The temple was dedicated in October 1984 and rededicated in March 1989 following a remodel.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <h3 className="text-[14px] font-semibold text-[#082f6d] mb-1">Temple Hours</h3>
                  <p className="text-[13px] text-gray-600 leading-relaxed">
                    Tuesday–Friday: 6:30 AM–8:45 PM
                    <br />
                    Saturday: 6:30 AM–7:45 PM
                    <br />
                    Sunday–Monday: Closed
                  </p>
                </div>
              </div>
            </>
          ) : isKaryaSiddhi ? (
            <>
              <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
                Karya Siddhi Hanuman Temple is a Hindu temple in Frisco, Texas, dedicated to Lord Hanuman and serving the Dallas–Fort Worth Hindu community. The temple is associated with His Holiness Sri Ganapathy Sachchidananda Swamiji and is part of the Datta Yoga Center tradition. The temple offers daily worship, puja, archana, abhishekam, festivals and other spiritual and community activities. The temple's presiding deity is Karya Siddhi Hanuman, and the temple is known for its distinctive Marakata Hanuman murti.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <h3 className="text-[14px] font-semibold text-[#082f6d] mb-1">Temple Timings</h3>
                  <p className="text-[13px] text-gray-600 leading-relaxed">
                    Monday–Friday: 8:00 AM–1:00 PM, 5:30 PM–8:30 PM
                    <br />
                    Saturday–Sunday: 8:00 AM–8:30 PM
                  </p>
                </div>
                <div>
                  <h3 className="text-[14px] font-semibold text-[#082f6d] mb-1">Worship & Services</h3>
                  <p className="text-[13px] text-gray-600 leading-relaxed max-w-[60ch]">
                    The temple offers a variety of religious services, including Hanuman Archana, Abhishekam, pujas, special sevas and festival celebrations. Puja schedules can vary by day and festival, so visitors should check the temple calendar for the latest timings before visiting.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {entry.name} is a {isSpiritualService ? 'spiritual services provider' : 'Hindu temple and place of worship'} serving the South Asian community across Dallas–Fort Worth
              {entry.city ? `, located in ${entry.city}, TX` : ''}.
              {!isSpiritualService && ' Visitors can join daily prayers, puja, darshan, aarti, and community festivals throughout the year.'}
              {entry.address ? ` Located at ${entry.address}.` : ''}
            </p>
          )}
          <div className="mt-6">
            {/* Instagram is rendered as a full button in the Contact & Info
                sidebar, so strip it here to avoid a duplicate small icon. */}
            <SocialLinks
              item={{ ...entry, website: undefined, instagram: undefined, instagramUrl: undefined, instagram_url: undefined }}
              px={20}
            />
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {entry.address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.address}</span>
              </div>
            )}
            {entry.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.phone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          {entry.instagram && (
            <a
              href={entry.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex items-center justify-center gap-2 text-center bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
            >
              <Instagram className="w-4 h-4" />
              Instagram
            </a>
          )}
          {entry.address && (
            <a
              href={mapsUrl(entry)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 block text-center bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              Get Directions
            </a>
          )}
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 pb-10">
        <Link
          to="/temples"
          className="inline-flex items-center gap-1.5 text-[#082f6d] font-semibold text-[14px] hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Temples
        </Link>
      </div>
    </div>
  );
}
